import { apiDelete, apiGet, apiPatch, apiPost } from './api'

/** Client for the community character board (/api/characters). */

export type PublicCharacter = {
  id: string
  title: string
  tagline: string
  imageUrl: string | null
  links: string[]
  description: string | null
  lorebook: string | null
  lorebookTokens?: number | null
  creator: string | null
  createdAt: string
}

export type CharacterFormBody = {
  title: string
  tagline: string
  imageUrl: string
  links: string[]
  description: string
  lorebook: string
  creator: string
  /** Quiet field, sent untouched by the real UI. */
  terms?: string
}

export type CreateCharacterResult = {
  character: PublicCharacter
  manageKey: string
}

export const fetchCharacters = (): Promise<{ characters: PublicCharacter[] }> =>
  apiGet('/characters')

export const fetchCharacter = (id: string): Promise<{ character: PublicCharacter }> =>
  apiGet(`/characters/${encodeURIComponent(id)}`)

export const createCharacter = (
  body: CharacterFormBody,
): Promise<CreateCharacterResult> => apiPost('/characters', body)

export const updateCharacter = (
  id: string,
  manageKey: string,
  body: CharacterFormBody,
): Promise<{ character: PublicCharacter }> =>
  apiPatch(`/characters/${encodeURIComponent(id)}`, body, {
    headers: { 'x-manage-key': manageKey },
  })

export const verifyManageKey = (id: string, manageKey: string): Promise<{ valid: boolean }> =>
  apiPost(
    `/characters/${encodeURIComponent(id)}/verify`,
    {},
    { headers: { 'x-manage-key': manageKey } },
  )

export const deleteCharacter = (id: string, manageKey: string): Promise<{ deleted: boolean }> =>
  apiDelete(`/characters/${encodeURIComponent(id)}`, { headers: { 'x-manage-key': manageKey } })

/** Returns a working manage key for this character: the stored one if present,
 *  otherwise asks the user for it and verifies against the API. Returns null
 *  when the user cancels or the key is wrong. */
export async function requestManageKey(id: string): Promise<string | null> {
  const existing = getManageKey(id)
  if (existing) return existing

  const entered = window.prompt(
    'Enter the manage key you received when this character was created.',
  )
  const key = entered?.trim()
  if (!key) return null

  try {
    await verifyManageKey(id, key)
    saveManageKey(id, key)
    return key
  } catch {
    window.alert('That manage key does not match this character.')
    return null
  }
}

// --- manage keys: kept in localStorage so creators can delete their own cards ---
const KEY_STORE = 's404:char-keys'

function readKeys(): Record<string, string> {
  try {
    const raw = localStorage.getItem(KEY_STORE)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Record<string, string>
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

export function saveManageKey(id: string, key: string): void {
  try {
    const keys = readKeys()
    keys[id] = key
    localStorage.setItem(KEY_STORE, JSON.stringify(keys))
  } catch {
    /* storage may be unavailable; the key was shown once in the UI */
  }
}

export function getManageKey(id: string): string | null {
  return readKeys()[id] ?? null
}

export function removeManageKey(id: string): void {
  try {
    const keys = readKeys()
    delete keys[id]
    localStorage.setItem(KEY_STORE, JSON.stringify(keys))
  } catch {
    /* ignore */
  }
}

// --- shared helpers ---

/** Hostname for link pills, e.g. chub.ai. Falls back to the trimmed URL. */
export function linkLabel(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}
